import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PortalSelectionPage from "./pages/PortalSelectionPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import PatientsPage from "./pages/PatientsPage";
import ClinicalVisitPage from "./pages/ClinicalVisitPage";
import BillingPage from "./pages/BillingPage";
import AIScribePage from "./pages/AIScribePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import KioskPage from "./pages/KioskPage";
import ClinicKioskPage from "./pages/ClinicKioskPage";
import MarketplacePage from "./pages/MarketplacePage";
import DoctorLabsStorePage from "./pages/DoctorLabsStorePage";
import SupportPage from "./pages/SupportPage";
import MRKioskPage from "./pages/MRKioskPage";
import MRVisitorsPage from "./pages/MRVisitorsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import GoogleFitDisclosure from "./pages/GoogleFitDisclosure";
import ClinicInventoryPage from "./pages/ClinicInventoryPage";
import ExpenseTrackerPage from "./pages/ExpenseTrackerPage";
import WalletPage from "./pages/WalletPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import InstallAppPage from "./pages/InstallAppPage";
import SSOLandingPage from "./pages/SSOLandingPage";
import PaymentResultPage from "./pages/PaymentResultPage";

// B2B Portal Pages
import SuperAdminLoginPage from "./pages/SuperAdminLoginPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminLIS from "./pages/SuperAdminLIS";
import AdminLabsStoreConfigPage from "./pages/AdminLabsStoreConfigPage";
import VendorLoginPage from "./pages/VendorLoginPage";
import VendorDashboard from "./pages/VendorDashboard";

// Patient Portal Pages
import PatientLoginPage from "./pages/PatientLoginPage";
import PatientDashboard from "./pages/PatientDashboard";
import PatientWalletPage from "./pages/PatientWalletPage";
import PatientMarketplacePage from "./pages/PatientMarketplacePage";
import PatientConsultationPage from "./pages/PatientConsultationPage";
import DoctorConsultationPage from "./pages/DoctorConsultationPage";

// QR Scan Landing
import ScanLandingPage from "./pages/ScanLandingPage";

// Face Vitals
import FaceVitalsPage from "./pages/FaceVitalsPage";
import DoctorFaceVitalsPage from "./pages/DoctorFaceVitalsPage";
import DoctorHealthTrackingPage from "./pages/DoctorHealthTrackingPage";
import DoctorMarketingPage from "./pages/DoctorMarketingPage";
import DoctorVendorBillsPage from "./pages/DoctorVendorBillsPage";
import PublicInvoicePage from "./pages/PublicInvoicePage";

// LIS (Lab Information System) Pages
import LISLayout from "./pages/lis/LISLayout";
import LISDashboard from "./pages/lis/LISDashboard";
import RateList from "./pages/lis/RateList";
import PanelsList from "./pages/lis/PanelsList";
import ProcessingLabsList from "./pages/lis/ProcessingLabsList";
import PatientsList from "./pages/lis/PatientsList";
import DoctorsList from "./pages/lis/DoctorsList";
import CentresList from "./pages/lis/CentresList";
import AgentsList from "./pages/lis/AgentsList";
import NewBill from "./pages/lis/NewBill";
import CasesList from "./pages/lis/CasesList";
import BillDetail from "./pages/lis/BillDetail";
import DailyReport from "./pages/lis/DailyReport";
import BusinessAnalysis from "./pages/lis/BusinessAnalysis";
import ReferralBusiness from "./pages/lis/ReferralBusiness";
import ExpensesList from "./pages/lis/ExpensesList";
import DataExport from "./pages/lis/DataExport";
import ReportUpload from "./pages/lis/ReportUpload";
import BillSettings from "./pages/lis/BillSettings";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-3 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-3 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <Routes>
      {/* Main Portal Selection - Entry Point */}
      <Route path="/" element={<PortalSelectionPage />} />
      <Route path="/invoice/:billId" element={<PublicInvoicePage />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/google-fit-disclosure" element={<GoogleFitDisclosure />} />
      
      {/* Install App Page - Shareable Link */}
      <Route path="/install" element={<InstallAppPage />} />
      <Route path="/download" element={<InstallAppPage />} />
      <Route path="/get" element={<InstallAppPage />} />
      <Route path="/sso" element={<SSOLandingPage />} />
      <Route path="/payment-result" element={<PaymentResultPage />} />
      
      {/* Doctor Portal Routes */}
      <Route path="/doctor/login" element={user ? <Navigate to="/doctor/dashboard" /> : <LoginPage />} />
      <Route path="/doctor/forgot-password" element={<ForgotPasswordPage userType="doctor" portalName="Doctor" onBack={() => window.location.href = '/doctor/login'} />} />
      <Route path="/doctor/reset-password" element={<ResetPasswordPage userType="doctor" portalName="Doctor" loginPath="/doctor/login" />} />
      <Route path="/doctor/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/doctor/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/doctor/patients" element={<ProtectedRoute roles={['doctor', 'assistant']}><PatientsPage /></ProtectedRoute>} />
      <Route path="/doctor/clinical" element={<ProtectedRoute roles={['doctor']}><ClinicalVisitPage /></ProtectedRoute>} />
      <Route path="/doctor/billing" element={<ProtectedRoute roles={['doctor', 'assistant']}><BillingPage /></ProtectedRoute>} />
      <Route path="/doctor/ai-scribe" element={<ProtectedRoute roles={['doctor']}><AIScribePage /></ProtectedRoute>} />
      <Route path="/doctor/marketplace" element={<ProtectedRoute roles={['doctor', 'assistant']}><MarketplacePage /></ProtectedRoute>} />
      <Route path="/doctor/labs-store" element={<ProtectedRoute roles={['doctor', 'assistant']}><DoctorLabsStorePage /></ProtectedRoute>} />
      <Route path="/doctor/analytics" element={<ProtectedRoute roles={['doctor']}><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/doctor/settings" element={<ProtectedRoute roles={['doctor']}><SettingsPage /></ProtectedRoute>} />
      <Route path="/doctor/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/doctor/patient-history" element={<Navigate to="/doctor/patients" />} />
      <Route path="/doctor/mr-visitors" element={<ProtectedRoute roles={['doctor']}><MRVisitorsPage /></ProtectedRoute>} />
      <Route path="/doctor/inventory" element={<ProtectedRoute roles={['doctor', 'assistant']}><ClinicInventoryPage /></ProtectedRoute>} />
      <Route path="/doctor/expenses" element={<ProtectedRoute roles={['doctor', 'assistant']}><ExpenseTrackerPage /></ProtectedRoute>} />
      <Route path="/doctor/wallet" element={<ProtectedRoute roles={['doctor']}><WalletPage /></ProtectedRoute>} />
      <Route path="/doctor/subscriptions" element={<ProtectedRoute roles={['doctor']}><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/doctor/online-consultation" element={<ProtectedRoute roles={['doctor']}><DoctorConsultationPage /></ProtectedRoute>} />
      <Route path="/doctor/face-vitals" element={<ProtectedRoute roles={['doctor']}><DoctorFaceVitalsPage /></ProtectedRoute>} />
      <Route path="/doctor/health-tracking" element={<ProtectedRoute roles={['doctor']}><DoctorHealthTrackingPage /></ProtectedRoute>} />
      <Route path="/doctor/marketing" element={<ProtectedRoute roles={['doctor']}><DoctorMarketingPage /></ProtectedRoute>} />
      <Route path="/doctor/vendor-bills" element={<ProtectedRoute roles={['doctor', 'assistant']}><DoctorVendorBillsPage /></ProtectedRoute>} />      
      {/* Legacy Doctor Routes (redirect to new paths) */}
      <Route path="/doctor" element={<Navigate to="/doctor/login" />} />
      <Route path="/login" element={<Navigate to="/doctor/login" />} />
      <Route path="/dashboard" element={<Navigate to="/doctor/dashboard" />} />
      <Route path="/appointments" element={<Navigate to="/doctor/appointments" />} />
      <Route path="/patients" element={<Navigate to="/doctor/patients" />} />
      <Route path="/clinical" element={<Navigate to="/doctor/clinical" />} />
      <Route path="/billing" element={<Navigate to="/doctor/billing" />} />
      <Route path="/ai-scribe" element={<Navigate to="/doctor/ai-scribe" />} />
      <Route path="/marketplace" element={<Navigate to="/doctor/marketplace" />} />
      <Route path="/analytics" element={<Navigate to="/doctor/analytics" />} />
      <Route path="/settings" element={<Navigate to="/doctor/settings" />} />
      <Route path="/support" element={<Navigate to="/doctor/support" />} />
      <Route path="/patient-history" element={<Navigate to="/doctor/patient-history" />} />
      <Route path="/mr-visitors" element={<Navigate to="/doctor/mr-visitors" />} />
      
      {/* Public Kiosk Routes */}
      <Route path="/kiosk/:token" element={<KioskPage />} />
      <Route path="/checkin/:clinicCode" element={<ClinicKioskPage />} />
      <Route path="/mr-kiosk/:clinicCode" element={<MRKioskPage />} />
      
      {/* Vendor Portal Routes */}
      <Route path="/vendor/login" element={<VendorLoginPage />} />
      <Route path="/vendor/forgot-password" element={<ForgotPasswordPage userType="vendor" portalName="Vendor" onBack={() => window.location.href = '/vendor/login'} />} />
      <Route path="/vendor/reset-password" element={<ResetPasswordPage userType="vendor" portalName="Vendor" loginPath="/vendor/login" />} />
      <Route path="/vendor/dashboard" element={<VendorDashboard />} />
      
      {/* LIS (Lab Management) Routes - nested under vendor */}
      <Route path="/vendor/lis" element={<LISLayout />}>
        <Route index element={<LISDashboard />} />
        <Route path="tests" element={<RateList />} />
        <Route path="panels" element={<PanelsList />} />
        <Route path="processing-labs" element={<ProcessingLabsList />} />
        <Route path="patients" element={<PatientsList />} />
        <Route path="doctors" element={<DoctorsList />} />
        <Route path="centres" element={<CentresList />} />
        <Route path="agents" element={<AgentsList />} />
        <Route path="new-bill" element={<NewBill />} />
        <Route path="cases" element={<CasesList />} />
        <Route path="bill/:billId" element={<BillDetail />} />
        <Route path="business/daily" element={<DailyReport />} />
        <Route path="business/analysis" element={<BusinessAnalysis />} />
        <Route path="business/referrals" element={<ReferralBusiness />} />
        <Route path="business/expenses" element={<ExpensesList />} />
        <Route path="business/export" element={<DataExport />} />
        <Route path="reports" element={<ReportUpload />} />
        <Route path="settings/bill" element={<BillSettings />} />
      </Route>
      
      <Route path="/vendor" element={<Navigate to="/vendor/login" />} />
      
      {/* Super Admin Portal Routes */}
      <Route path="/admin/login" element={<SuperAdminLoginPage />} />
      <Route path="/admin/dashboard" element={<SuperAdminDashboard />} />
      <Route path="/admin/lis" element={<SuperAdminLIS />} />
      <Route path="/admin/labs-store" element={<AdminLabsStoreConfigPage />} />
      <Route path="/super-admin" element={<Navigate to="/admin/login" />} />
      <Route path="/super-admin/login" element={<Navigate to="/admin/login" />} />
      <Route path="/super-admin/forgot-password" element={<ForgotPasswordPage userType="admin" portalName="Super Admin" onBack={() => window.location.href = '/admin/login'} />} />
      <Route path="/super-admin/reset-password" element={<ResetPasswordPage userType="admin" portalName="Super Admin" loginPath="/admin/login" />} />
      <Route path="/super-admin/dashboard" element={<Navigate to="/admin/dashboard" />} />
      
      {/* Face Vitals */}
      <Route path="/face-vitals" element={<FaceVitalsPage />} />

      {/* QR Scan Landing Route */}
      <Route path="/scan/:barcode" element={<ScanLandingPage />} />
      
      {/* Patient Portal Routes */}
      <Route path="/patient/login" element={<PatientLoginPage />} />
      <Route path="/patient/dashboard" element={<PatientDashboard />} />
      <Route path="/patient/wallet" element={<PatientWalletPage />} />
      <Route path="/patient/marketplace" element={<PatientMarketplacePage />} />
      <Route path="/patient/consultation" element={<PatientConsultationPage />} />
      <Route path="/patient" element={<Navigate to="/patient/login" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-right" richColors />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
